import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { toast } from 'sonner';
import { feedPosts as seedPosts, recognitionFeed as seedKudos, articles as seedArticles, featuredStories as seedFeatured, mustReads as seedMustReads, FeedPost, Recognition, Article, FeaturedStory, MustRead } from './data';
// Marketing/admin passcode. This is a lightweight, client-side gate suited to a
// no-login intranet — it keeps the site open to everyone while restricting the
// Site Admin panel to people who know the marketing code.
export const ADMIN_CODE = 'INDY-MKTG';
const ADMIN_STORAGE_KEY = 'ihl_admin_unlocked';
const USER_STORAGE_KEY = 'ihl_user';
export interface CurrentUser {
  name: string;
  title: string;
  location: string;
  email: string;
  avatar: string;
}
export interface SiteConfig {
  welcome: {
    eyebrow: string;
    heading: string;
    subtext: string;
  };
  announcement: {
    enabled: boolean;
    message: string;
    tone: 'gold' | 'navy';
  };
  modules: {
    mustReads: boolean;
    rateWatch: boolean;
    featuredAutoplay: boolean;
  };
  hiddenNav: string[];
}
export interface ArticleInput {
  category: string;
  title: string;
  excerpt: string;
  author: string;
  authorAvatar: string;
  readTime: string;
  image: string;
  body: string[];
}
export interface MustReadInput {
  title: string;
  category: string;
  due: string;
  icon: any;
}
interface AppState {
  user: CurrentUser;
  updateUser: (patch: Partial<CurrentUser>) => boolean;
  // Per-browser identity (no-password "who are you")
  hasIdentity: boolean;
  // True when the visitor chose "Just browsing" — read-only, no identity yet.
  isBrowsingOnly: boolean;
  startBrowsing: () => void;
  // Ensures the visitor has a real identity before an action that posts as them.
  // Returns true if they're good to go; otherwise re-opens the welcome gate and
  // returns false so the caller can bail out.
  requireIdentity: () => boolean;
  persistUser: (user: CurrentUser) => boolean;
  setIdentity: (user: CurrentUser) => boolean;
  clearIdentity: () => void;
  isAdmin: boolean;
  unlockAdmin: (code: string) => boolean;
  lockAdmin: () => void;
  config: SiteConfig;
  updateConfig: (patch: Partial<SiteConfig>) => void;
  toggleNavItem: (label: string) => void;
  // Content
  articles: Article[];
  addArticle: (input: ArticleInput) => void;
  updateArticle: (id: string, input: ArticleInput) => void;
  removeArticle: (id: string) => void;
  featured: FeaturedStory[];
  addFeatured: (articleId: string, accent: string) => void;
  updateFeaturedAccent: (id: string, accent: string) => void;
  removeFeatured: (id: string) => void;
  mustReads: MustRead[];
  addMustRead: (input: MustReadInput) => void;
  updateMustRead: (id: string, input: MustReadInput) => void;
  removeMustRead: (id: string) => void;
  // Social
  posts: FeedPost[];
  addPost: (input: {
    body: string;
    audience: string;
    image?: string;
  }) => void;
  addComment: (postId: string, body: string) => void;
  kudos: Recognition[];
  addKudos: (input: {
    to: string;
    toAvatar: string;
    value: string;
    message: string;
  }) => void;
  composerOpen: boolean;
  openComposer: () => void;
  closeComposer: () => void;
}
const AppContext = createContext<AppState | null>(null);
const defaultUser: CurrentUser = {
  name: 'Alex Morgan',
  title: 'Loan Officer',
  location: 'Dallas, TX',
  email: 'alex.morgan@indephl.com',
  avatar: 'https://i.pravatar.cc/160?img=8'
};
const defaultConfig: SiteConfig = {
  welcome: {
    eyebrow: 'Independence EX',
    heading: 'Welcome home.',
    subtext: 'Your home base for company news, people, and everything happening across Independence Home Loans.'
  },
  announcement: {
    enabled: false,
    message: 'Reminder: Q3 All-Hands Town Hall is this Thursday at 10:00 AM CT.',
    tone: 'gold'
  },
  modules: {
    mustReads: true,
    rateWatch: true,
    featuredAutoplay: true
  },
  // "People" is hidden from the sidebar by default. Marketing can re-enable it
  // from Site Admin → Navigation.
  hiddenNav: ['People']
};
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2073&auto=format&fit=crop';
function readAdminUnlocked(): boolean {
  try {
    return window.localStorage.getItem(ADMIN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}
function readSavedUser(): CurrentUser | null {
  try {
    const raw = window.localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.name === 'string') return parsed as CurrentUser;
    return null;
  } catch {
    return null;
  }
}
// Returns true only if the write actually succeeded (so callers can warn the
// user instead of silently continuing when storage is full or blocked).
function writeSavedUser(user: CurrentUser): boolean {
  try {
    window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    return true;
  } catch (err) {
    console.warn('Could not persist profile to localStorage:', err);
    return false;
  }
}
export function AppProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<CurrentUser>(defaultUser);
  const [hasIdentity, setHasIdentity] = useState<boolean>(false);
  const [isBrowsingOnly, setIsBrowsingOnly] = useState<boolean>(false);
  const [config, setConfig] = useState<SiteConfig>(defaultConfig);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [posts, setPosts] = useState<FeedPost[]>(seedPosts);
  const [kudos, setKudos] = useState<Recognition[]>(seedKudos);
  const [articles, setArticles] = useState<Article[]>(seedArticles);
  const [featured, setFeatured] = useState<FeaturedStory[]>(seedFeatured);
  const [mustReads, setMustReads] = useState<MustRead[]>(seedMustReads);
  const [composerOpen, setComposerOpen] = useState(false);
  // Restore identity + admin unlock from a previous session on this browser.
  useEffect(() => {
    const saved = readSavedUser();
    if (saved) {
      setUser(saved);
      setHasIdentity(true);
    }
    if (readAdminUnlocked()) setIsAdmin(true);
  }, []);
  // Write-only helper so a caller can test persistence before committing.
  const persistUser = useCallback((next: CurrentUser) => {
    return writeSavedUser(next);
  }, []);
  const setIdentity = useCallback((next: CurrentUser) => {
    const ok = writeSavedUser(next);
    setUser(next);
    setHasIdentity(true);
    setIsBrowsingOnly(false);
    return ok;
  }, []);
  // Enter read-only mode: dismiss the welcome gate without creating an identity.
  const startBrowsing = useCallback(() => {
    setIsBrowsingOnly(true);
  }, []);
  // Gate posting actions. If the visitor has a real identity, allow the action.
  // Otherwise, re-open the welcome gate so they can introduce themselves first.
  const requireIdentity = useCallback(() => {
    if (hasIdentity) return true;
    setIsBrowsingOnly(false);
    toast('Tell us who you are first', {
      description: 'Add your name so your post shows up as you.'
    });
    return false;
  }, [hasIdentity]);
  const clearIdentity = useCallback(() => {
    setHasIdentity(false);
    setIsBrowsingOnly(false);
    setUser(defaultUser);
    try {
      window.localStorage.removeItem(USER_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);
  const unlockAdmin = useCallback((code: string) => {
    const ok = code.trim().toUpperCase() === ADMIN_CODE;
    if (ok) {
      setIsAdmin(true);
      try {
        window.localStorage.setItem(ADMIN_STORAGE_KEY, 'true');
      } catch {
        /* ignore */
      }
    }
    return ok;
  }, []);
  const lockAdmin = useCallback(() => {
    setIsAdmin(false);
    try {
      window.localStorage.removeItem(ADMIN_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);
  const updateUser = useCallback((patch: Partial<CurrentUser>) => {
    let ok = true;
    setUser(u => {
      const next = {
        ...u,
        ...patch
      };
      // Persist edits so a returning visitor keeps their profile.
      ok = writeSavedUser(next);
      return next;
    });
    return ok;
  }, []);
  const updateConfig = useCallback((patch: Partial<SiteConfig>) => {
    setConfig(c => ({
      ...c,
      ...patch
    }));
  }, []);
  const toggleNavItem = useCallback((label: string) => {
    setConfig(c => ({
      ...c,
      hiddenNav: c.hiddenNav.includes(label) ? c.hiddenNav.filter(l => l !== label) : [...c.hiddenNav, label]
    }));
  }, []);
  /* -------------------------------- Articles ------------------------------- */
  const addArticle = useCallback((input: ArticleInput) => {
    const id = `art-${Date.now()}`;
    setArticles(prev => [{
      id,
      date: 'Just now',
      ...input,
      image: input.image || FALLBACK_IMAGE
    }, ...prev]);
  }, []);
  const updateArticle = useCallback((id: string, input: ArticleInput) => {
    setArticles(prev => prev.map(a => a.id === id ? {
      ...a,
      ...input,
      image: input.image || a.image
    } : a));
    setFeatured(prev => prev.map(f => f.id === id ? {
      ...f,
      category: input.category,
      title: input.title,
      excerpt: input.excerpt,
      author: input.author,
      image: input.image || f.image
    } : f));
  }, []);
  const removeArticle = useCallback((id: string) => {
    setArticles(prev => prev.filter(a => a.id !== id));
    setFeatured(prev => prev.filter(f => f.id !== id));
  }, []);
  /* -------------------------------- Featured ------------------------------- */
  const addFeatured = useCallback((articleId: string, accent: string) => {
    setArticles(currentArticles => {
      const src = currentArticles.find(a => a.id === articleId);
      if (src) {
        setFeatured(prev => {
          if (prev.some(f => f.id === src.id)) return prev;
          return [{
            id: src.id,
            category: src.category,
            title: src.title,
            excerpt: src.excerpt,
            author: src.author,
            date: src.date,
            image: src.image,
            accent
          }, ...prev];
        });
      }
      return currentArticles;
    });
  }, []);
  const updateFeaturedAccent = useCallback((id: string, accent: string) => {
    setFeatured(prev => prev.map(f => f.id === id ? {
      ...f,
      accent
    } : f));
  }, []);
  const removeFeatured = useCallback((id: string) => {
    setFeatured(prev => prev.filter(f => f.id !== id));
  }, []);
  /* ------------------------------- Must reads ------------------------------ */
  const addMustRead = useCallback((input: MustReadInput) => {
    setMustReads(prev => [...prev, {
      id: `mr-${Date.now()}`,
      ...input
    }]);
  }, []);
  const updateMustRead = useCallback((id: string, input: MustReadInput) => {
    setMustReads(prev => prev.map(m => m.id === id ? {
      ...m,
      ...input
    } : m));
  }, []);
  const removeMustRead = useCallback((id: string) => {
    setMustReads(prev => prev.filter(m => m.id !== id));
  }, []);
  /* --------------------------------- Social -------------------------------- */
  const addPost = useCallback(({
    body,
    audience,
    image
  }: {
    body: string;
    audience: string;
    image?: string;
  }) => {
    setPosts(prev => [{
      id: `post-${Date.now()}`,
      author: user.name,
      role: user.title,
      avatar: user.avatar,
      time: 'Just now',
      team: audience,
      body,
      image,
      likes: 0,
      comments: []
    }, ...prev]);
  }, [user]);
  const addComment = useCallback((postId: string, body: string) => {
    if (!requireIdentity()) return;
    setPosts(prev => prev.map(p => p.id === postId ? {
      ...p,
      comments: [...p.comments, {
        id: `c-${Date.now()}`,
        author: user.name,
        avatar: user.avatar,
        time: 'Just now',
        body
      }]
    } : p));
  }, [user, requireIdentity]);
  const addKudos = useCallback(({
    to,
    toAvatar,
    value,
    message
  }: {
    to: string;
    toAvatar: string;
    value: string;
    message: string;
  }) => {
    if (!requireIdentity()) return;
    setKudos(prev => [{
      id: `kudo-${Date.now()}`,
      from: user.name,
      fromAvatar: user.avatar,
      to,
      toAvatar,
      value,
      message,
      likes: 0,
      time: 'Just now'
    }, ...prev]);
  }, [user, requireIdentity]);
  const openComposer = useCallback(() => {
    // Browsing-only visitors must set an identity before they can post.
    if (!requireIdentity()) return;
    setComposerOpen(true);
  }, [requireIdentity]);
  const closeComposer = useCallback(() => setComposerOpen(false), []);
  return <AppContext.Provider value={{
    user,
    updateUser,
    hasIdentity,
    isBrowsingOnly,
    startBrowsing,
    requireIdentity,
    persistUser,
    setIdentity,
    clearIdentity,
    isAdmin,
    unlockAdmin,
    lockAdmin,
    config,
    updateConfig,
    toggleNavItem,
    articles,
    addArticle,
    updateArticle,
    removeArticle,
    featured,
    addFeatured,
    updateFeaturedAccent,
    removeFeatured,
    mustReads,
    addMustRead,
    updateMustRead,
    removeMustRead,
    posts,
    addPost,
    addComment,
    kudos,
    addKudos,
    composerOpen,
    openComposer,
    closeComposer
  }} data-id="element-817">
      {children}
    </AppContext.Provider>;
}
export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}